// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Link, useNavigate } from "@tanstack/react-router";
import { Construction, ExternalLink, Filter, Fingerprint, Search, Shield, ShieldCheck, Users, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RealmPlusIcon } from "@/components/icons";
import { PageHeader } from "@/components/layout/page-header";
import { DateValue } from "@/components/shared/datetime-value";
import { FilterToggle } from "@/components/shared/filter-toggle";
import { StatusIndicator } from "@/components/shared/status-indicator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

interface SsoRealm {
  id: string;
  name: string;
  provider: "SAML" | "OIDC";
  issuer: string;
  userCount: number;
  isActive: boolean;
  chains: string[];
  createdAt: string;
}

const MOCK_REALMS: SsoRealm[] = [
  {
    id: "walmart",
    name: "Walmart Inc.",
    provider: "OIDC",
    issuer: "https://sso.walmart.com",
    userCount: 312,
    isActive: false,
    chains: ["walmart", "walmart_ca"],
    createdAt: "2024-06-22T00:00:00Z",
  },
  {
    id: "morrisons",
    name: "Morrisons",
    provider: "SAML",
    issuer: "https://login.morrisons.com/saml2",
    userCount: 74,
    isActive: true,
    chains: ["morrisons"],
    createdAt: "2025-02-10T00:00:00Z",
  },
  {
    id: "lidl",
    name: "Lidl Stiftung & Co. KG",
    provider: "SAML",
    issuer: "https://login.lidl.com/saml2",
    userCount: 156,
    isActive: true,
    chains: ["lidl_gb", "lidl_fr"],
    createdAt: "2025-05-18T00:00:00Z",
  },
];

const PROVIDER_STYLES: Record<string, string> = {
  SAML: "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800",
  OIDC: "bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-950 dark:text-violet-300 dark:border-violet-800",
};

export function SsoList() {
  const { t } = useTranslation("back-office");
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [onlyActive, setOnlyActive] = useState(true);

  const filtered = useMemo(() => {
    let result = MOCK_REALMS;
    if (onlyActive) result = result.filter((r) => r.isActive);
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.id.toLowerCase().includes(q) ||
          r.provider.toLowerCase().includes(q) ||
          r.issuer.toLowerCase().includes(q),
      );
    }
    return result;
  }, [search, onlyActive]);

  return (
    <div>
      <PageHeader title={t("sso.pageTitle")} icon={Fingerprint} />
      <div className="p-6 space-y-5">
        <div className="flex items-center gap-2 px-4 py-2 rounded-md border border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-400 text-xs">
          <Construction className="h-3.5 w-3.5 shrink-0" />
          {t("activity.wipBanner")}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("sso.searchPlaceholder", "Search realms...")}
              className="pl-9"
            />
          </div>
          <FilterToggle
            active={onlyActive}
            onChange={() => setOnlyActive((v) => !v)}
            icon={Filter}
            label={t("common.active")}
          />
          {(search || !onlyActive) && (
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 text-muted-foreground hover:text-foreground"
              onClick={() => {
                setSearch("");
                setOnlyActive(true);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
          <div className="ml-auto">
            <Button onClick={() => toast.warning(t("common.workInProgress"))}>
              <RealmPlusIcon className="h-4 w-4" />
              <Construction className="h-4 w-4" />
              Create Realm
            </Button>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">No realms found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((realm) => (
              <Card
                key={realm.id}
                className="relative overflow-hidden transition-colors hover:border-primary/40 hover:bg-accent/50 cursor-pointer"
                onClick={() => navigate({ to: "/iam/sso/$realmId", params: { realmId: realm.id } })}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-primary/10 shrink-0">
                        {realm.isActive ? (
                          <ShieldCheck className="h-4.5 w-4.5 text-primary" />
                        ) : (
                          <Shield className="h-4.5 w-4.5 text-muted-foreground" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <CardTitle className="text-sm font-semibold truncate">{realm.name}</CardTitle>
                        <span className="text-xs text-muted-foreground font-mono">{realm.id}</span>
                      </div>
                    </div>
                    <StatusIndicator active={realm.isActive} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="secondary" className={PROVIDER_STYLES[realm.provider]}>
                      {realm.provider}
                    </Badge>
                    {realm.chains.map((chain) => (
                      <Link
                        key={chain}
                        to="/chains/$chainName"
                        params={{ chainName: chain }}
                        className="text-xs font-mono text-primary hover:underline"
                      >
                        {chain}
                      </Link>
                    ))}
                  </div>

                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground truncate">
                    <ExternalLink className="h-3 w-3 shrink-0" />
                    <span className="truncate font-mono">{realm.issuer}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <Users className="h-3.5 w-3.5" />
                      {realm.userCount} {realm.userCount === 1 ? "user" : "users"}
                    </div>
                    <span className="flex items-center gap-1">
                      Created <DateValue value={realm.createdAt} />
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
